import { useMutation } from '@apollo/client/react';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Switch,
} from 'antd';
import clsx from 'clsx';
import { filter, groupBy, includes, map, max } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { AppContext } from '../../../../../../../AppContext';
import {
  ADDONS,
  GA_EVENT,
  GA_LABEL,
  REGISTER_STATUS_LABEL,
  REQUEST_FEATURE_UPGRADE_KEYS,
} from '../../../../../../../common/constants';
import { Event } from '../../../../../../../common/trackEvents';
import { formValidatorRules } from '../../../../../../../common/utils';
import CommonDropdown from '../../../../../../../components/CommonDropdown';
import UpgradeModal from '../../../../../../../components/UpgradeModal';
import { UPDATE_REGISTER_APPROVERS } from '../../../../../graphql/Mutation';
import {
  REGISTER_EXTERNAL_USER_APPROVER_DROPDOWN_LIST,
  REGISTER_INTERNAL_APPROVER_DROPDOWN_LIST,
} from '../../../../../graphql/Queries';

const { characterWithoutWhiteSpace } = formValidatorRules;

function RegisterApprovalModal(props) {
  const {
    isUpdate,
    registerData,
    registerId,
    isEditable,
    refetchRef,
    affectedLogsCount,
    onClose,
  } = props;
  const { getCurrentUser, getTenantUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const tenantUser = getTenantUser();
  const numberOfAllowedLevel = Array.from({ length: 3 }, (_, i) => i + 1);

  const approversByLevel = groupBy(registerData?.approvers, (b) => {
    return b?.levelNumber;
  });
  const [form] = Form.useForm();
  const { projectId } = useParams();
  const [selectedAllInternalIndex, setSelectedAllInternalIndex] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureKey, setFeatureKey] = useState();
  const [selectedAllExternalIndex, setSelectedAllExternalIndex] = useState([]);
  const [needApproval, setNeedApproval] = useState(false);
  const [allowedLevels, setAllowedLevels] = useState([1]);
  const [levelsToBeVisible, setLevelsToBeVisible] = useState([1]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const [updateRegisterApprovers, { loading: updateLoading }] = useMutation(
    UPDATE_REGISTER_APPROVERS,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.EDIT_PROJECT_REGISTER_APPROVERS, {
          label: GA_LABEL.EDIT_PROJECT_REGISTER_APPROVERS,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          project_id: projectId,
          register_id: registerId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        refetchRef?.current?.getRegister();
        onClose?.();
      },
    },
  );

  const onSubmitFinish = async (formValues) => {
    const allowedApprovers = filter(formValues?.approvers, (_, index) =>
      includes(allowedLevels, index),
    );
    const newFormValues = map(allowedApprovers, (record, index) => ({
      ...record,
      levelNumber: index + 1,
      internalApproverIds: includes(selectedAllInternalIndex, index + 1)
        ? []
        : record?.internalApproverIds,
      externalUserApproverIds: includes(selectedAllExternalIndex, index + 1)
        ? []
        : record?.externalUserApproverIds,
      selectedAllInternalApprovers: includes(
        selectedAllInternalIndex,
        index + 1,
      ),
      selectedAllExternalApprovers: includes(
        selectedAllExternalIndex,
        index + 1,
      ),
    }));
    const variables = {
      data: {
        approvalLevelData: newFormValues,
        needApproval: formValues?.needApproval,
      },
      registerId,
    };
    try {
      await updateRegisterApprovers({
        variables: { ...variables },
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (registerData) {
      setNeedApproval(registerData?.needApproval);
      form.setFieldsValue({
        needApproval: registerData?.needApproval,
      });
      const groupData = groupBy(registerData?.approvers, (b) => {
        return b?.levelNumber;
      });
      if (registerData?.approvers?.length > 0) {
        const maxLevelKeys = Object?.keys(groupData)?.map((e) => Number(e));
        const maxLevel = max(maxLevelKeys);
        setAllowedLevels(maxLevelKeys);
        if (maxLevel < numberOfAllowedLevel?.length) {
          setLevelsToBeVisible([...maxLevelKeys, maxLevel + 1]);
        } else {
          setLevelsToBeVisible(maxLevelKeys);
        }
      }
      map(
        Object?.keys(groupData)?.map((e) => Number(e)),
        (id) => {
          form.setFieldsValue({
            [`needLevel${id}Approver`]: true,
          });
        },
      );

      for (let index = 1; index <= 3; index += 1) {
        form.setFieldsValue({
          approvers: {
            [index]: {
              externalUserApproverIds: filter(
                map(
                  groupData?.[index],
                  (element) => element?.externalUserApproverId,
                ),
                (id) => id,
              ),
              internalApproverIds: filter(
                map(
                  groupData?.[index],
                  (element) => element?.internalApproverId,
                ),
                (id) => id,
              ),
              levelName: groupData?.[index]?.[0]?.levelName,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerData]);

  const handleInternalDeselect = (index) => {
    form.setFieldsValue({
      approvers: { [index]: { internalApproverIds: [] } },
    });
  };

  useEffect(() => {
    if (selectedAllInternalIndex?.length > 0) {
      map(selectedAllInternalIndex, (index) => {
        form.setFieldsValue({
          approvers: { [index]: { internalApproverIds: ['all'] } },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAllInternalIndex]);

  const handleExternalDeselect = (index) => {
    form.setFieldsValue({
      approvers: { [index]: { externalUserApproverIds: [] } },
    });
  };
  useEffect(() => {
    if (selectedAllExternalIndex?.length > 0) {
      map(selectedAllExternalIndex, (index) => {
        form.setFieldsValue({
          approvers: { [index]: { externalUserApproverIds: ['all'] } },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAllExternalIndex]);

  return (
    <div className="add-approver-modal">
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmitFinish}
        disabled={registerData?.status === REGISTER_STATUS_LABEL?.ARCHIVED}
      >
        <div className={clsx('config-div')}>
          <Row
            justify="space-between notification-row"
            align="middle"
            gutter={{ xs: 0, sm: 30 }}
          >
            <Col span={18}>
              <div className="title">Approval required</div>
              <div className="description">
                User has to send for approval before finalizing the register log
              </div>
            </Col>
            <Col span={4}>
              <div className="d-flex justify-center align-center">
                <Form.Item name="needApproval" valuePropName="checked">
                  <Switch
                    disabled={!isEditable}
                    onChange={(e) => {
                      if (
                        includes(
                          tenantUser?.tenant?.addOn,
                          ADDONS?.MULTI_LEVEL_APPROVAL,
                        )
                      ) {
                        setNeedApproval(e);
                        form.setFieldsValue({
                          needLevel1Approver: true,
                        });
                      } else if (e && !registerData?.needApproval) {
                        form.setFieldsValue({
                          needApproval: false,
                        });
                        message.destroy();
                        message.error(
                          'Your current plan has restriction and requires upgrade.',
                        );
                        //! commented for future use
                        // setShowUpgradeModal(true);
                        // setFeatureKey(
                        //   REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL,
                        // );
                      }
                    }}
                    id="need-approval-switch"
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </div>
        {needApproval && (
          <div
            className={clsx(
              'add-approval-form mt-24',
              isDropdownVisible && 'hide-overflow',
            )}
          >
            {map(numberOfAllowedLevel, (index) => {
              return (
                <div key={index} hidden={!includes(levelsToBeVisible, index)}>
                  <div className="d-flex align-center mb-10 justify-between">
                    <div className="d-flex align-center">
                      <div className="title mr-10">{`Level ${index}`}</div>
                      <Form.Item
                        name={`needLevel${index}Approver`}
                        className="mb-0"
                        valuePropName="checked"
                      >
                        <Switch
                          disabled={
                            includes(allowedLevels, index + 1) ||
                            !includes(allowedLevels, index - 1) ||
                            !isEditable
                          }
                          onClick={(e) => {
                            if (e) {
                              if (
                                (includes(
                                  Object.keys(approversByLevel),
                                  String(index),
                                ) ||
                                  index <=
                                    tenantUser?.tenant
                                      ?.registerMaxApprovalLevel) &&
                                includes(
                                  tenantUser?.tenant?.addOn,
                                  ADDONS?.MULTI_LEVEL_APPROVAL,
                                )
                              ) {
                                setAllowedLevels((prev) => [...prev, index]);
                              } else if (
                                !includes(
                                  Object.keys(approversByLevel),
                                  String(index),
                                )
                              ) {
                                form.setFieldsValue({
                                  [`needLevel${index}Approver`]: false,
                                });
                                //! commented for future use
                                // setFeatureKey(
                                //   REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL_MAX_LEVEL,
                                // );
                                // setShowUpgradeModal(true);
                                message.destroy();
                                message.error(
                                  'Your current plan has restriction and requires upgrade.',
                                );
                              }
                            } else {
                              setAllowedLevels(
                                filter(
                                  allowedLevels,
                                  (level) => level !== index,
                                ),
                              );
                            }
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <Form.Item
                    name={['approvers', index, 'levelName']}
                    dependencies={[`needLevel${index}Approver`]}
                    rules={[
                      characterWithoutWhiteSpace,
                      {
                        required: true && includes(allowedLevels, index),
                        message: 'Missing Level name',
                      },
                      {
                        max: includes(allowedLevels, index) && 30,
                        message: 'Name cannot be more than 30 characters',
                      },
                    ]}
                    label="Name"
                    initialValue={`Level ${index}`}
                  >
                    <Input
                      disabled={!includes(allowedLevels, index) || !isEditable}
                      placeholder="Level Name"
                      id={`level-${index}-name-input`}
                    />
                  </Form.Item>

                  <Form.Item
                    className="mt-10"
                    name={['approvers', index, 'internalApproverIds']}
                    dependencies={[
                      ['approvers', index, 'externalUserApproverIds'],
                      [`needLevel${index}Approver`],
                    ]}
                    rules={[
                      ({ getFieldValue }) => ({
                        required:
                          !getFieldValue([
                            'approvers',
                            index,
                            'externalUserApproverIds',
                          ])?.length && includes(allowedLevels, index),
                        message: 'Please select approver',
                        type: 'array',
                      }),
                    ]}
                    label={
                      <div className="d-flex justify-between width-percent-100">
                        <span>Internal Approvers</span>
                        {includes(selectedAllInternalIndex, index) && (
                          <span
                            className="text-primary pointer"
                            role="button"
                            tabIndex="0"
                            onClick={() => {
                              setSelectedAllInternalIndex(
                                filter(
                                  selectedAllInternalIndex,
                                  (i) => i !== index,
                                ),
                              );
                              handleInternalDeselect(index);
                            }}
                          >
                            Deselect All
                          </span>
                        )}
                      </div>
                    }
                    normalize={(value) => {
                      if (value?.length > 0) {
                        if (index + 1 <= numberOfAllowedLevel?.length) {
                          setLevelsToBeVisible((prev) => [...prev, index + 1]);
                        }
                      }
                      if (value.includes('all')) {
                        setSelectedAllInternalIndex((prev) => [...prev, index]);
                        setIsDropdownVisible(false);
                        return ['all'];
                      }
                      return value;
                    }}
                  >
                    <CommonDropdown
                      onDropdownVisibleChange={(e) => setIsDropdownVisible(e)}
                      mode="multiple"
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      placeholder="Select Internal Approvers"
                      query={REGISTER_INTERNAL_APPROVER_DROPDOWN_LIST}
                      variables={{
                        filter: {
                          projectId,
                          registerId,
                        },
                      }}
                      responsePath="registerInternalApproverDropdownList.data"
                      valuePath="id"
                      labelPath="user.name"
                      optionKey="internal-approver"
                      fetchPolicy="network-only"
                      disabled={
                        includes(selectedAllInternalIndex, index) ||
                        !includes(allowedLevels, index) ||
                        !isEditable
                      }
                      isSelectedAll={includes(selectedAllInternalIndex, index)}
                      hasSelectAll
                      getPopupContainer={() =>
                        // eslint-disable-next-line no-undef
                        document.querySelector('.add-approver-modal')
                      }
                      onChange={() => {
                        form.validateFields([
                          ['approvers', index, 'externalUserApproverIds'],
                        ]);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={['approvers', index, 'externalUserApproverIds']}
                    dependencies={[
                      ['approvers', index, 'internalApproverIds'],
                      [`needLevel${index}Approver`],
                    ]}
                    rules={[
                      ({ getFieldValue }) => ({
                        required:
                          !getFieldValue([
                            'approvers',
                            index,
                            'internalApproverIds',
                          ])?.length && includes(allowedLevels, index),
                        message: 'Please select approver',
                        type: 'array',
                      }),
                    ]}
                    label={
                      <div className="d-flex justify-between width-percent-100">
                        <span>External Approvers</span>
                        {includes(selectedAllExternalIndex, index) && (
                          <span
                            className="text-primary pointer"
                            role="button"
                            tabIndex="0"
                            onClick={() => {
                              setSelectedAllExternalIndex(
                                filter(
                                  selectedAllExternalIndex,
                                  (i) => i !== index,
                                ),
                              );
                              handleExternalDeselect(index);
                            }}
                          >
                            Deselect All
                          </span>
                        )}
                      </div>
                    }
                    normalize={(value) => {
                      if (value?.length > 0) {
                        if (index + 1 <= numberOfAllowedLevel?.length) {
                          setLevelsToBeVisible((prev) => [...prev, index + 1]);
                        }
                      }
                      if (value.includes('all')) {
                        setSelectedAllExternalIndex((prev) => [...prev, index]);
                        setIsDropdownVisible(false);
                        return ['all'];
                      }
                      return value;
                    }}
                  >
                    <CommonDropdown
                      onDropdownVisibleChange={(e) => setIsDropdownVisible(e)}
                      mode="multiple"
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      placeholder="Select External Approvers"
                      query={REGISTER_EXTERNAL_USER_APPROVER_DROPDOWN_LIST}
                      variables={{
                        filter: {
                          projectId,
                          registerId,
                        },
                      }}
                      responsePath="registerExternalUserApproverDropdownList.data"
                      valuePath="id"
                      labelPath={['name', 'agency.name']}
                      optionKey="external-approver"
                      fetchPolicy="network-only"
                      disabled={
                        includes(selectedAllExternalIndex, index) ||
                        !includes(allowedLevels, index) ||
                        !isEditable
                      }
                      isSelectedAll={includes(selectedAllExternalIndex, index)}
                      hasSelectAll
                      getPopupContainer={() =>
                        // eslint-disable-next-line no-undef
                        document.querySelector('.add-approver-modal')
                      }
                      onChange={() => {
                        form.validateFields([
                          ['approvers', index, 'internalApproverIds'],
                        ]);
                      }}
                    />
                  </Form.Item>
                  {includes(levelsToBeVisible, index + 1) &&
                    numberOfAllowedLevel?.length > index && <Divider dashed />}
                </div>
              );
            })}
          </div>
        )}
      </Form>

      {isEditable && (
        <div className="form-buttons mt-5">
          {registerData?.status !== REGISTER_STATUS_LABEL?.ARCHIVED && (
            <Popconfirm
              title={
                registerData?.status === REGISTER_STATUS_LABEL.LIVE &&
                affectedLogsCount > 0
                  ? `This will apply the new workflow to ${affectedLogsCount} existing log${affectedLogsCount !== 1 ? 's' : ''}. Continue?`
                  : undefined
              }
              onConfirm={() => form.submit()}
              okText="Yes"
              cancelText="Cancel"
              disabled={
                registerData?.status !== REGISTER_STATUS_LABEL.LIVE ||
                affectedLogsCount === 0
              }
            >
              <Button
                shape="round"
                type="primary"
                className="save-button"
                htmlType={
                  registerData?.status !== REGISTER_STATUS_LABEL.LIVE ||
                  affectedLogsCount === 0
                    ? 'submit'
                    : 'button'
                }
                onClick={
                  registerData?.status !== REGISTER_STATUS_LABEL.LIVE ||
                  affectedLogsCount === 0
                    ? () => form.submit()
                    : undefined
                }
                loading={updateLoading}
                id="register-approver-save-button"
              >
                {isUpdate ? 'Save' : 'Add'}
              </Button>
            </Popconfirm>
          )}
        </div>
      )}
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          projectId={projectId}
          featureKey={featureKey}
          setFeatureKey={setFeatureKey}
          isAlreadyRequested={
            tenantUser?.tenant?.featureApprovalRequestConfig?.[
              featureKey === REQUEST_FEATURE_UPGRADE_KEYS.STAGE_APPROVAL
                ? 'stageApprovalRequestSent'
                : 'stageApprovalMaxLevelRequestSent'
            ]
          }
        />
      )}
    </div>
  );
}

export default RegisterApprovalModal;
